/**
 * Big Five Profile — question glossary.
 *
 * Why this exists
 * ---------------
 *   The IPIP-NEO-120 is a validated psychometric instrument and we do
 *   NOT modify or annotate the items themselves (doing so would change
 *   what's being measured and break comparability with public norms).
 *
 *   However, a small number of items use English vocabulary that is
 *   genuinely unfamiliar to some respondents — idioms ("feel blue"),
 *   slightly archaic verbs ("plunge into"), or culturally-specific
 *   terms ("liberal political candidates"). When a respondent doesn't
 *   know what a word means, they can't answer accurately, which
 *   *itself* introduces measurement noise.
 *
 *   This file provides plain vocabulary definitions only. Two strict
 *   rules applied below:
 *
 *     1. Definitions explain the WORD, not the QUESTION. We never tell
 *        the respondent how to interpret an item or what a "high"
 *        answer means — that would bias the response.
 *
 *     2. Definitions are deliberately neutral. For politically-charged
 *        terms ("liberal", "conservative") the gloss describes the
 *        cluster of positions associated with the label, not whether
 *        those positions are good or bad.
 *
 * Matching
 * --------
 *   Match is case-insensitive and whole-word (or whole-phrase) only.
 *   Multi-word keys are matched verbatim (with normalised whitespace
 *   and apostrophes). Order of keys in the rendered list follows the
 *   order of first occurrence in the question text, so the gloss reads
 *   left-to-right with the statement.
 */

const GLOSSARY_ENTRIES = [
  // Idioms
  {
    term: 'feel blue',
    aliases: ['often feel blue'],
    definition: 'Idiom — to feel sad or low.',
  },
  {
    term: 'down in the dumps',
    definition: 'Idiom — feeling sad, low, or unhappy.',
  },
  {
    term: 'sharp tongue',
    definition: 'Idiom — a habit of speaking harshly or critically.',
  },
  {
    term: 'good word',
    aliases: ['a good word'],
    definition:
      'Idiom — something kind or positive to say about someone.',
  },
  {
    term: 'flights of fantasy',
    aliases: ['wild flights of fantasy'],
    definition: 'Imaginative, far-fetched ideas or daydreams.',
  },
  {
    term: 'plunge into',
    definition:
      'To start doing something with full energy, without holding back.',
  },

  // Slightly literary verbs
  {
    term: 'radiate',
    definition: 'To visibly give off or express something (e.g. joy).',
  },
  {
    term: 'obstruct',
    definition: "To get in the way of or block something from happening.",
  },
  {
    term: 'boast',
    definition: 'To talk proudly about your own achievements or qualities.',
  },

  // Vocabulary that often trips non-native speakers
  {
    term: 'binges',
    aliases: ['binge', 'go on binges'],
    definition:
      'A binge is a short period of doing something to excess — '
      + 'e.g. eating too much, drinking too much, or shopping a lot in one go.',
  },
  {
    term: 'overindulge',
    definition:
      'To have too much of something pleasurable (food, drink, etc.).',
  },
  {
    term: 'cravings',
    definition: 'Strong urges or desires, often for food, drink, or other treats.',
  },
  {
    term: 'reckless',
    definition: 'Acting without thinking about danger or consequences.',
  },
  {
    term: 'virtues',
    definition: 'Good qualities or moral strengths.',
  },
  {
    term: 'the needy',
    definition: 'People in difficult circumstances who need help.',
  },
  {
    term: 'the homeless',
    definition: 'People who do not have a place to live.',
  },

  // Abstract / academic vocabulary
  {
    term: 'abstract ideas',
    aliases: ['abstract'],
    definition:
      'General concepts and theories (e.g. justice, infinity) rather '
      + 'than specific objects or facts.',
  },
  {
    term: 'philosophical',
    definition:
      'To do with big questions about life, meaning, ethics, or how '
      + 'to think about the world.',
  },

  // Political vocabulary — culture-neutral framing
  {
    term: 'liberal political candidates',
    aliases: ['liberal'],
    definition:
      'Candidates who generally favour social change, broader '
      + 'government services, and progressive policies. (Neutral '
      + 'description of the label — not an endorsement.)',
  },
  {
    term: 'conservative political candidates',
    aliases: ['conservative'],
    definition:
      'Candidates who generally favour tradition, smaller '
      + 'government, and stability. (Neutral description of the '
      + 'label — not an endorsement.)',
  },
];

/**
 * Build a flat lookup of {normalisedKey: {term, definition}} so that
 * findGlossaryMatches can do a single pass over the question text.
 * Aliases share the same definition as their canonical term and use
 * the alias spelling as the displayed key.
 */
function _buildLookup() {
  const lookup = [];
  for (const entry of GLOSSARY_ENTRIES) {
    const allKeys = [entry.term, ...(entry.aliases || [])];
    for (const key of allKeys) {
      lookup.push({
        key,
        normalisedKey: _normalise(key),
        definition: entry.definition,
      });
    }
  }
  // Match longest keys first so "down in the dumps" wins over "down".
  lookup.sort((a, b) => b.normalisedKey.length - a.normalisedKey.length);
  return lookup;
}

function _normalise(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'") // smart-quotes -> '
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

const _LOOKUP = _buildLookup();

/**
 * Find glossary matches in a question's text.
 * Returns an array of { displayKey, definition } in the order the
 * matched terms first appear in the text. Each definition is returned
 * at most once even if the term appears multiple times.
 */
export function findGlossaryMatches(text) {
  if (!text) return [];
  const normalisedText = _normalise(text);
  const seenDefinitions = new Set();
  const matches = [];
  for (const { key, normalisedKey, definition } of _LOOKUP) {
    if (seenDefinitions.has(definition)) continue;
    // Whole-word / whole-phrase match using a regex with word boundaries
    // where possible. For multi-word keys we still rely on simple
    // substring with surrounding whitespace/punctuation guards.
    const pattern = new RegExp(
      `(^|[^a-z0-9'])${_escapeRegex(normalisedKey)}([^a-z0-9']|$)`,
      'i',
    );
    if (pattern.test(normalisedText)) {
      matches.push({ displayKey: key, definition });
      seenDefinitions.add(definition);
    }
  }
  return matches;
}

function _escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
